import { useForm, Link } from "@inertiajs/react";
import { BadgeCheck, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import FormField, { FieldLabel } from "@/components/forms/FormField";
import { TIMEZONES, timezoneLabel, suggestedTimezone, localTimeIn } from "@/lib/timezones";
import CampaignPlanForm from "@/components/schools/setup/CampaignPlanForm";
import InviteAdminForm from "@/components/schools/setup/InviteAdminForm";
import SquareConnectAgreement from "@/components/schools/SquareConnectAgreement";
import { DoneSummary } from "@/components/schools/setup/StepPanel";
import { GuideTip, StepIntro, StepNotice } from "@/components/schools/setup/SetupPrimitives";
import { formatMoney, formatNumber, pluralize } from "@/lib/format";

// ── Pay the campaign fee ──────────────────────────────────────

// The fee for this campaign's plan, and the way to the payment page
// (plan choice, total, e-Transfer details). Done once PagePledge approves
// the campaign.
export function PaymentStep({ step, setup }) {
  const { payment } = setup;
  if (!payment) return null;

  if (step.status === "done") {
    return (
      <DoneSummary title={payment.complimentary ? "No fee for your school" : "Paid and approved"}>
        {payment.complimentary
          ? "Your school doesn't pay campaign fees, so this campaign is approved."
          : `${payment.paid_plan.label}, ${formatMoney(payment.paid_plan.price_cents)} paid. Connect Square, invite teachers and launch whenever you're ready.`}
      </DoneSummary>
    );
  }

  const plan = payment.quoted_plan;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-4 rounded-xl border border-border bg-background px-5 py-4">
        <div>
          <p className="font-medium text-foreground">{plan.name} plan</p>
          <p className="text-sm text-muted-foreground">
            {plan.student_limit ? `Up to ${formatNumber(plan.student_limit)} students` : "No student limit"} · you have {pluralize(payment.student_count, "student")}
          </p>
        </div>
        <p className="font-display text-3xl tabular-nums text-foreground">{formatMoney(plan.price_cents)} <span className="font-sans text-sm text-muted-foreground">CAD</span></p>
      </div>
      <StepIntro>
        <p>
          On the payment page you can choose a bigger plan if more students are still to come, and you'll get the e-Transfer details. We approve your campaign when the payment arrives.
        </p>
      </StepIntro>
      <Button size="lg" asChild>
        <Link href={payment.payment_path}>
          <BadgeCheck className="h-4 w-4" />
          Choose your plan and pay
        </Link>
      </Button>
    </div>
  );
}

// ── 1. School details ─────────────────────────────────────────

// Always the form: confirming the details the first time ("These are
// right"), then a normal form whose Save lights up when something changes.
export function SchoolDetailsStep({ step, setup }) {
  return <SchoolDetailsForm setup={setup} confirmed={step.status === "done"} />;
}

function SchoolDetailsForm({ setup, confirmed }) {
  const { school, provinces, paths } = setup;
  const form = useForm({
    name: school.name || "",
    city: school.city || "",
    province: school.province || "",
    timezone: school.timezone,
    campaign: setup.campaign?.slug || "",
  });
  const { data, setData, errors, processing } = form;
  const suggestion = suggestedTimezone(data.province);
  const localTime = localTimeIn(data.timezone);

  const submit = (e) => {
    e.preventDefault();
    form.patch(paths.update_details, { preserveScroll: true, preserveState: "errors" });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <FormField
        name="name"
        label="School name"
        required
        value={data.name}
        onChange={(e) => setData("name", e.target.value)}
        error={errors.name}
        help="Exactly as families know it — it's on every donation page, receipt and sheet."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField name="city" label="City" value={data.city} onChange={(e) => setData("city", e.target.value)} error={errors.city} />
        <SelectField
          name="province"
          label="Province or territory"
          value={data.province}
          onChange={(value) => setData("province", value)}
          options={provinces.map((p) => ({ value: p, label: p }))}
        />
      </div>
      <SelectField
        name="timezone"
        label="Timezone"
        value={data.timezone}
        onChange={(value) => setData("timezone", value)}
        options={TIMEZONES.some((tz) => tz.value === school.timezone) ? TIMEZONES : [{ value: school.timezone, label: timezoneLabel(school.timezone) }, ...TIMEZONES]}
        help={[
          localTime && `It's ${localTime} in this timezone right now. It should match the clock at your school.`,
          setup.campaign && `Changing it keeps ${setup.campaign.name} on the same dates; they just start and end at midnight in the new zone.`,
        ].filter(Boolean).join(" ")}
      />
      {data.timezone !== suggestion && (
        <StepNotice
          tone="info"
          action={
            <Button type="button" size="sm" variant="outline" onClick={() => setData("timezone", suggestion)}>
              Use {timezoneLabel(suggestion).split(" (")[0]}
            </Button>
          }
        >
          Most schools in {data.province} use {timezoneLabel(suggestion)} time. Keep your choice if your school is in a different zone.
        </StepNotice>
      )}
      <div className="flex flex-wrap gap-3 border-t border-border pt-5">
        {confirmed ? (
          <>
            <Button type="submit" disabled={processing || !form.isDirty}>{processing ? "Saving…" : "Save changes"}</Button>
            {form.isDirty ? (
              <Button type="button" variant="ghost" onClick={() => form.reset()}>Discard changes</Button>
            ) : (
              <p className="self-center text-xs text-muted-foreground">Saved. It's {school.local_time} at your school.</p>
            )}
          </>
        ) : (
          <Button type="submit" disabled={processing}>{processing ? "Saving…" : "These are right"}</Button>
        )}
      </div>
    </form>
  );
}

function SelectField({ name, label, value, onChange, options, help }) {
  return (
    <div className="space-y-2">
      <FieldLabel fieldId={name} label={label} help={help} />
      <select
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={help ? `${name}-help` : undefined}
        className="flex h-11 w-full rounded-lg border border-input bg-card px-4 py-2.5 text-base text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

// ── 2. The campaign ───────────────────────────────────────────

// The form is the step: fill it in to create the campaign, and once it
// exists the same fields stay here, filled in, to change until it starts.
// The draft a school gets at signup has placeholder dates, so the form
// suggests dates as if creating it, and saving confirms them.
export function CampaignStep({ setup }) {
  const campaign = setup.campaign_details;
  const mode = !campaign ? "create" : campaign.dates_confirmed ? "edit" : "confirm";
  return <CampaignPlanForm mode={mode} setup={setup} />;
}

// ── 3. Square ─────────────────────────────────────────────────

const SQUARE_INTROS = {
  square: "You told us your school already uses Square. Whoever owns that Square account should do this step, often the treasurer or office manager.",
  stripe: "You mentioned your school uses Stripe. PagePledge takes donations through Square, so you'll need a Square account as well. Opening one is free; Square takes its standard fee on each donation, which donors can choose to cover.",
  none: "You'll need a Square account for your school or parent council. Opening one is free: Square asks for your organization's details and a bank account, then verifies them.",
};

export function SquareStep({ step, setup }) {
  const { square, paths, application } = setup;
  // Waiting on the campaign fee; StepPanel explains (docs/BUSINESS_RULES.md §2a).
  if (step.status === "blocked") return null;

  if (step.status === "done") {
    return (
      <div className="space-y-5">
        <DoneSummary
          title={`Connected to ${square.location_name || "Square"}`}
          actions={
            <Button size="sm" variant="outline" asChild>
              <Link href={paths.settings}>
                <Settings className="h-4 w-4" />
                Manage in Settings
              </Link>
            </Button>
          }
        >
          Donations go straight to this Square location{square.connected_by ? `. Connected by ${square.connected_by}` : ""}
          {square.connected_at ? ` on ${new Date(square.connected_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}` : ""}.
        </DoneSummary>
        <GuideTip title="Do a test run once donations open">
          Make a small donation on a student's page and check it arrives in Square. Refund it from your Square dashboard afterwards and it drops out of your totals.
        </GuideTip>
      </div>
    );
  }

  const intro = SQUARE_INTROS[application?.payment_processor] || SQUARE_INTROS.none;

  return (
    <div className="space-y-6">
      <StepIntro><p>{intro}</p></StepIntro>

      <ol className="space-y-3 text-sm">
        {[
          ["Click Connect Square", "and sign in with your school's Square login."],
          ["Approve PagePledge's access.", "Square shows exactly what it allows: taking donations for your school and confirming they went through."],
          ["Pick the location", "donations should go to. Most schools have just one."],
        ].map(([lead, rest], i) => (
          <li key={lead} className="flex gap-3">
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">{i + 1}</span>
            <span className="leading-relaxed text-muted-foreground"><strong className="font-medium text-foreground">{lead}</strong> {rest}</span>
          </li>
        ))}
      </ol>

      <StepNotice tone="warning">
        Make sure the Square account is fully activated, with its bank account verified. Until it is, card payments won't go through.
      </StepNotice>

      <SquareConnectAgreement href={paths.connect_square}>
        {application?.payment_processor !== "square" && (
          <a href="https://squareup.com/ca/en" target="_blank" rel="noopener" className="text-sm font-medium text-primary hover:underline">
            Open a Square account
          </a>
        )}
      </SquareConnectAgreement>

      <div className="rounded-xl border border-border p-5">
        <p className="font-medium text-foreground">Someone else has the Square login?</p>
        <p className="mb-4 mt-1 text-sm text-muted-foreground">
          Invite them as a co-organizer. They'll get a sign-in link and can connect Square from here themselves.
        </p>
        <InviteAdminForm path={paths.members} />
      </div>

      <GuideTip title="Card details never touch PagePledge">
        Donors' cards go straight to Square, and Square pays out to your bank on its usual schedule, usually the next business day.
      </GuideTip>
    </div>
  );
}
